// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import type { SourceCardDto } from '../api/client.js';
import { playableRecitation } from './recitation.js';
import { messages, useMessages } from '../i18n.js';
import { RecitationButton } from './RecitationButton.js';

/**
 * Verified sources render inside the reply's flow (founder direction 2026-08-25) while remaining
 * their own component: the words are provider text in a visually distinct block, never blended
 * into the assistant's prose string, and every field the
 * provider gave is present: dropping a hadith's grade or a verse's explanation misrepresents the
 * source a reviewer approved (RELIGIOUS_CONTENT_PIPELINE; the reviewer verifies the grade as part
 * of approval).
 *
 * The founder's reading rule (2026-08-25): the source text carries the card, one small line names
 * where it came from and its grade, and the full bibliographic apparatus folds away until asked
 * for. Present-but-folded keeps both truths: nothing the provider gave is dropped, and nobody
 * meets a wall of citations before the words themselves.
 *
 * In this development build every card carries synthetic content, and the UI says so plainly.
 */

/** The short source line: the first bibliographic segment, never the whole apparatus. */
export function shortSource(attribution: string): string {
  const first = attribution.split(/[،.]/u)[0]?.trim() ?? '';
  return first.length > 0 && first.length <= 80 ? first : `${attribution.slice(0, 60).trim()}…`;
}

/**
 * Which way a card's own text runs. The page is Arabic and right-to-left; a card in another language
 * that inherits both is laid out backwards and read aloud in the wrong voice.
 *
 * The list is the right-to-left scripts a source card can plausibly carry, matched on the language
 * subtag so a regional variant is treated the same as its base.
 */
const RIGHT_TO_LEFT = Object.freeze(['ar', 'fa', 'ur', 'he', 'ps', 'sd', 'ku', 'dv']);

export function directionFor(language: string): 'rtl' | 'ltr' {
  const [subtag = ''] = language.toLowerCase().split('-');
  return RIGHT_TO_LEFT.includes(subtag) ? 'rtl' : 'ltr';
}

/**
 * The source explorer (ADR-028 stage 6).
 *
 * It is deliberately **not** an entry in the interaction registry, and no model may name it or
 * trigger it. Everything it reveals is a field that was already fetched from an approved provider
 * and verified before the card existed: the provider, the identifier a reviewer approved, the
 * version that was fetched, the checksum the words were matched against, and the window the
 * approval covers. Nothing here is generated, summarised or explained by a model — which is the
 * only way a "tell me more about this source" affordance can exist in this product at all.
 *
 * This is also the one place a person can see why the words are trustworthy rather than being asked
 * to take it on faith.
 */
const PROVIDER_NAMES: Readonly<Record<string, string>> = Object.freeze({
  quran_foundation: 'Quran Foundation',
  quranenc: 'QuranEnc',
  hadeethenc: 'HadeethEnc',
});

/** Their own names, not translated: an invented Arabic name for an organisation would be wrong. */
export function providerName(provider: string): string {
  return PROVIDER_NAMES[provider] ?? provider;
}

/** The day, without a clock: a person checking provenance wants the date, not a timestamp. */
export function dayOf(instant: string): string {
  const day = instant.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/u.test(day) ? day : instant;
}

/** Enough of the digest to compare, never so much that it becomes the line. */
export function shortChecksum(checksum: string): string {
  return checksum.length > 16 ? `${checksum.slice(0, 16)}…` : checksum;
}

/**
 * The seal on the fold (founder, 2026-09-13: "the verification chain treated as a seal").
 *
 * It is drawn rather than written as generated CSS content, for the same reason the microphone is:
 * a `content: '✓'` is real text in the accessibility tree on some screen readers, and a stray tick
 * announced before "source and verification" is noise. This is a picture, marked as one, and it
 * makes no claim the wording beside it does not already make.
 */
function SealIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="source-seal" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 12.4l2.6 2.6L16 9.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A rendering of the meaning, beside the Arabic (founder, 2026-09-13).
 *
 * It is never the source and is not presented as one: the verse and the matn are the Arabic above
 * it. This is one named group's reading of what those words mean, supplied by the provider and
 * credited to them, so a reader who cannot read Arabic can follow the card without being told that
 * a translation is the text.
 *
 * It carries its own `lang` and `dir` because the card now holds two languages, and a block marked
 * with only one of them is laid out in the wrong direction — the exact defect the founder caught in
 * the conversation itself.
 */
function Rendering({
  card,
  label,
}: {
  readonly card: SourceCardDto;
  readonly label: string;
}): React.JSX.Element | null {
  const rendered = card.translationOrExplanation;
  const language = card.translationLanguage ?? null;
  if (rendered === null || rendered.trim().length === 0 || language === null) return null;
  return (
    <div className="source-rendering" lang={language} dir={directionFor(language)}>
      <p className="field-label">{label}</p>
      <p className="rendering-words">{rendered}</p>
      {card.translationEdition !== null && card.translationEdition !== undefined && (
        <p className="rendering-credit">{card.translationEdition}</p>
      )}
    </div>
  );
}

function IntegrityRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): React.JSX.Element {
  return (
    <div className="integrity-row">
      <dt>{label}</dt>
      <dd dir="ltr">{value}</dd>
    </div>
  );
}

const T = messages({
  ar: {
    group: 'المصدر الموثق',
    relation: 'الصلة بحديثك',
    fold: 'المصدر والتحقق',
    explanation: 'الشرح: ',
    chain: 'سلسلة التحقق',
    rendering: 'ترجمة المعنى',
    provider: 'المصدر',
    identifier: 'المعرّف',
    providerVersion: 'إصدار المزوّد',
    checksum: 'البصمة',
    fetchedAt: 'جُلب في',
    approvedUntil: 'الاعتماد صالح حتى',
    note: 'هذه الكلمات جاءت من المصدر أعلاه بهذا المعرّف، وطوبقت مع بصمتها قبل عرضها. بلال لا يكتب نص المصدر ولا يعيد صياغته ولا يترجمه.',
  },
  en: {
    group: 'Verified source',
    relation: 'How this connects',
    fold: 'Source and verification',
    explanation: 'Explanation: ',
    chain: 'Verification chain',
    rendering: 'Translation of the meaning',
    provider: 'Provider',
    identifier: 'Identifier',
    providerVersion: 'Provider version',
    checksum: 'Checksum',
    fetchedAt: 'Fetched on',
    approvedUntil: 'Approved until',
    note: 'These words came from the source above under that identifier, and were matched against its checksum before being shown. Bilal does not write the source text, rephrase it or translate it.',
  },
});

export function SourceCards({
  cards,
}: {
  readonly cards: readonly SourceCardDto[];
}): React.JSX.Element | null {
  const t = useMessages(T);
  if (cards.length === 0) return null;
  return (
    <div role="group" aria-label={t.group} className="source-flow">
      {cards.map((card) => {
        const recitation = playableRecitation(card);
        return (
          <div key={card.approvalId} className="woven-source">
            {typeof card.relation === 'string' && card.relation.length > 0 && (
              // The reply's own sentence on why this source stands here (founder, 2026-09-15),
              // before the words it introduces. Its direction follows its own first strong
              // character, like every sentence the model wrote.
              <p className="source-relation" dir="auto">
                <span className="field-label">{t.relation}</span>
                {card.relation}
              </p>
            )}
            {card.kind === 'quran' ? (
              // The verse is woven into the running text: mushaf-styled words inside the talk,
              // the reference riding after them — never a boxed card interrupting the reply.
              <>
                <p className="verse-inline" lang={card.language} dir={directionFor(card.language)}>
                  <span className="verse-words">﴿{card.content}﴾</span>
                  <span className="verse-ref"> [{card.canonicalReference}]</span>
                  {recitation !== null && (
                    <RecitationButton src={recitation} reference={card.canonicalReference} />
                  )}
                </p>
                <Rendering card={card} label={t.rendering} />
              </>
            ) : (
              <blockquote
                className="hadith-inline"
                lang={card.language}
                dir={directionFor(card.language)}
              >
                <p className="hadith-words">{card.content}</p>
                <Rendering card={card} label={t.rendering} />
                <p className="source-line">
                  {shortSource(card.attribution)}
                  {card.grade !== null && <span className="grade-chip"> · {card.grade}</span>}
                </p>
              </blockquote>
            )}
            <details className="source-fold">
              <summary>
                <SealIcon />
                {t.fold}
              </summary>
              {card.translationOrExplanation !== null && card.translationLanguage == null && (
                <p className="explanation">
                  <span className="field-label">{t.explanation}</span>
                  {card.translationOrExplanation}
                </p>
              )}
              {/* Named as a region rather than by putting a role on the <dl>: overriding a
                  description list's own role orphans its <dt>/<dd> pairs, which axe catches and a
                  screen reader would too. */}
              <section aria-label={t.chain}>
                <dl className="integrity">
                  <IntegrityRow label={t.provider} value={providerName(card.provider)} />
                  <IntegrityRow label={t.identifier} value={card.providerContentId} />
                  <IntegrityRow label={t.providerVersion} value={card.providerVersion} />
                  <IntegrityRow label={t.checksum} value={shortChecksum(card.checksum)} />
                  <IntegrityRow label={t.fetchedAt} value={dayOf(card.retrievedAt)} />
                  <IntegrityRow label={t.approvedUntil} value={dayOf(card.expiresAt)} />
                </dl>
              </section>
              <p className="integrity-note">{t.note}</p>
            </details>
          </div>
        );
      })}
    </div>
  );
}
